import React, { useEffect, useMemo, useState } from "react";
import Header from './Header/index';
import { Divider, Grid, makeStyles } from "@material-ui/core";
import styles from './styles';
import { getI18n, getI18nFormattedMessage } from "../../../utils/intl";
import { useIntl } from "react-intl";
import { AlertDialog, AlertMessages, FormActionButtons, Loading, TabPanel, TabsContainer } from "../../../components";
import { findIndex } from "lodash";
import { useHistory, useParams } from "react-router";
import OverView from "./Overview";
import Apis from "./Apis";
import Organizations from "./Organizations";
import { useDispatch, useSelector } from "react-redux";
import { createApiPlan, editApiPlan, fetchApiPlanDetails } from "../../../actions/apiPlans";
import { getApiPlanCreateData, getApiPlanDetails, getApiPlansValidationError, getApiPlansLoading, getUpdateApiPlanDetails, getApiPlansError, getApisUpdateForApiPlans, getOrgsUpdateForApiPlans, getDeleteApiPlan } from "../../../reducers/apiPlans";
import {
  ALERT_SUCCESS,
  PROCEED_TEXT,
  QUOTA_INTERVAL_DAY,
} from "../../../constants";
import { ROUTES } from "../../../constants/routes";
import { getFeatureFlagApiPlans, getIsPortalAdmin, getIsApiOwner } from "../../../reducers/portalConfig";

const ApiPlanDetails = () => {

  const [currentTab, setCurrentTab] = useState('add-api-plans-overview-tab');
  const [enableQuotaCheck, setEnableQuotaCheck] = useState(false);
  const [enableRateLimitCheck, setEnableRateLimitCheck] = useState(false);
  const [alertMessages, setAlertMessages] = useState({
    message: '',
    variant: '',
    status: false,
  })
  const [payload, setPayload] = useState({
    name: "",
    rateLimit: "",
    quota: "",
    quotaInterval: "",
    description: "",
  })
  const [APIsTabDirty, setAPIsTabDirty] = useState(false);
  const [orgsTabDirty, setOrgsTabDirty] = useState(false);
  const [redirectTabValue, setRedirectTabValue] = useState();
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const useStyles = makeStyles(styles);
  const dispatch = useDispatch();
  const classes = useStyles();
  const intl = getI18n(useIntl());
  const params = useParams();
  const history = useHistory();

  const isEditFlow = history.location.pathname !== "/admin/apis/api-plans/add";
  const isApiPlanCreationSuccessData = useSelector(getApiPlanCreateData);
  const isApiPlanUpdated = useSelector(getUpdateApiPlanDetails);
  const isUpdateApisForApiPlansSuccess = useSelector(getApisUpdateForApiPlans);
  const isUpdateOrgsForApiPlansSuccess = useSelector(getOrgsUpdateForApiPlans);
  const isApiPlanDeleteSuccess = useSelector(getDeleteApiPlan);
  const apiPlanDetails = useSelector(getApiPlanDetails);
  const isLoading = useSelector(getApiPlansLoading);
  const apiPlansValidationError = useSelector(getApiPlansValidationError);
  const apiPlansError = useSelector(getApiPlansError);
  const isApiPlansEnabled = useSelector(getFeatureFlagApiPlans);
  const isApiPlanErrorValidationMessage = apiPlansValidationError &&
    apiPlansValidationError[0]?.error;
  const isPortalAdmin = useSelector(getIsPortalAdmin);
  const isApiOwner = useSelector(getIsApiOwner);

  const apiPlanUuidFromEditFlow = params?.apiPlanUuid;
  const apiPlanUuid = isApiPlanCreationSuccessData && isApiPlanCreationSuccessData?.uuid ||
    apiPlanUuidFromEditFlow;

  const tabItems = [
    {
      id: 'overview',
      tabId: 'add-api-plans-overview-tab',
      label: getI18nFormattedMessage('label.overview'),
      visible: true,
    },
    {
      id: 'apis',
      tabId: 'add-api-plans-apis-tab',
      label: getI18nFormattedMessage('label.apis'),
      visible: true,
      disabled: !apiPlanUuid,
    },
    {
      id: 'organizations',
      tabId: 'add-api-plans-organizations-tab',
      label: getI18nFormattedMessage("label.proxy.details.page.overview.organizations"),
      visible: true,
      disabled: !apiPlanUuid,
    },
  ];

  const changeActiveTab = (tabId) => {
    setCurrentTab(tabId);
    const index = findIndex(tabItems, tab => tab.tabId === tabId);
    if (apiPlanUuid) {
      history.push(`/admin/apis/api-plans/details/${apiPlanUuid}/${tabItems[index].id}`);
    } else {
      history.push(`/admin/apis/api-plans/add/${tabItems[index].id}`);
    }
  };

  const handleTabChange = (event) => {
    const tabId = event.currentTarget.id;
    let currentTabDirty = false;
    if (currentTab === 'add-api-plans-apis-tab') {
      currentTabDirty = APIsTabDirty;
    } else if (currentTab === 'add-api-plans-organizations-tab') {
      currentTabDirty = orgsTabDirty;
    } else {
      currentTabDirty = isOverviewTabDirty;
    }
    if (currentTabDirty) {
      setRedirectTabValue(tabId);
      setShowUnsavedDialog(true);
    } else {
      changeActiveTab(tabId);
    }
  };

  const onDialogClose = () => {
    setShowUnsavedDialog(false);
  };
  const onDialogSubmit = () => {
    setShowUnsavedDialog(false);
    changeActiveTab(redirectTabValue);
  };

  const handleSaveApiPlan = () => {
    setAlertMessages({
      message: "",
      variant: "",
      status: false,
    })

    if (payload.quota && !payload.quotaInterval) {
      payload.quotaInterval = QUOTA_INTERVAL_DAY;
    }

    if (apiPlanUuid) {
      payload.uuid = apiPlanUuid;
      if (!enableRateLimitCheck) {
        payload.rateLimit = null;
      }

      if (!enableQuotaCheck) {
        payload.quota = null;
        payload.quotaInterval = null;
      }
      dispatch(editApiPlan(payload, apiPlanUuid));
    } else {
      dispatch(createApiPlan(payload));
    }
  }

  const handleCancel = () => {
    if (!isEditFlow) {
      window.location.href = ROUTES.apiPlans.path;
    }
  }

  const showAlertMessages = (variant, message) => {
    setAlertMessages({
      status: true,
      variant: variant,
      message: message,
    })
  }

  useEffect(() => {
    if (isApiPlanErrorValidationMessage) {
      showAlertMessages("error", isApiPlanErrorValidationMessage)
    }
  }, [isApiPlanErrorValidationMessage])

  useEffect(() => {
    if(!isPortalAdmin && !isApiOwner) {
      history.push('/404');
    }
  }, [])

  useEffect(() => {
    if (apiPlansError) {
      showAlertMessages("error", apiPlansError)
    }
  }, [apiPlansError])

  const deleteApiPlanSuccessMessage = intl.getI18nMessage("label.api.plans.delete.api.plan.success.message");
  const createApiPlanSuccessMessage = intl.getI18nMessage("label.api.plans.creation.success.message");
  const updateApiPlanSuccessMessage = intl.getI18nMessage("label.api.plans.update.success.message");

  useEffect(() => {
    if (isApiPlanCreationSuccessData) {
      showAlertMessages("success", createApiPlanSuccessMessage)
    }
  }, [isApiPlanCreationSuccessData])

  useEffect(() => {
    if (isApiPlanUpdated) {
      showAlertMessages("success", updateApiPlanSuccessMessage)
    }
  }, [isApiPlanUpdated])

  useEffect(() => {
    if (isUpdateApisForApiPlansSuccess) {
      showAlertMessages("success", updateApiPlanSuccessMessage)

    }
  }, [isUpdateApisForApiPlansSuccess])

  useEffect(() => {
    if (isUpdateOrgsForApiPlansSuccess) {
      showAlertMessages("success", updateApiPlanSuccessMessage)
    }
  }, [isUpdateOrgsForApiPlansSuccess])

  useEffect(() => {
    if (isApiPlanDeleteSuccess) {
      showAlertMessages("success", deleteApiPlanSuccessMessage)
    }
  }, [isApiPlanDeleteSuccess])

  useEffect(() => {
    if (isApiPlanDeleteSuccess) {
      if (alertMessages.message === deleteApiPlanSuccessMessage) {
        window.location.href = ROUTES.apiPlans.path;
      }
    }
  }, [isApiPlanDeleteSuccess, alertMessages.message, deleteApiPlanSuccessMessage])

  useEffect(() => {
    if (isApiPlanCreationSuccessData && apiPlanUuid) {
      history.push(`/admin/apis/api-plans/details/${apiPlanUuid}`);
    }
  }, [isApiPlanCreationSuccessData]);

  useEffect(() => {
    if (apiPlanUuid || isApiPlanCreationSuccessData || isApiPlanUpdated) {
      dispatch(fetchApiPlanDetails(apiPlanUuid));
    }
  }, [apiPlanUuid, isApiPlanCreationSuccessData, isApiPlanUpdated]);

  const resetPayload = () => {
    if (apiPlanDetails) {
      setPayload({
        name: apiPlanDetails.name,
        description: apiPlanDetails.description,
        quota: apiPlanDetails.quota,
        quotaInterval: apiPlanDetails.quotaInterval,
        rateLimit: apiPlanDetails.rateLimit,
      });
      setEnableQuotaCheck(!!apiPlanDetails.quota);
      setEnableRateLimitCheck(!!apiPlanDetails.rateLimit);
    }
  };

  useEffect(() => {
    resetPayload();
  }, [currentTab]);

  useEffect(() => {
    resetPayload();
  }, [apiPlanDetails])

  useEffect(() => {
    if (!isApiPlansEnabled) {
      window.location.href = ROUTES.apiPlansEnable.path;
    }
  }, [isApiPlansEnabled])

  const isOverviewTabDirty = useMemo(() => {
    const { name, description, rateLimit, quota, quotaInterval } = payload;
    const { name: existingName, description: existingDescription, rateLimit: existingRateLimit,
      quota: existingQuota, quotaInterval: existingQuotaInterval } = apiPlanDetails || {};

    return !apiPlanUuid
      ? !!name || !!rateLimit || !!quota
      : name !== existingName ||
      description !== existingDescription ||
      enableRateLimitCheck !== !!existingRateLimit ||
      enableQuotaCheck !== !!existingQuota ||
      (enableRateLimitCheck && Number(rateLimit) !== Number(existingRateLimit)) ||
      (enableQuotaCheck && Number(quota) !== Number(existingQuota)) ||
      quotaInterval !== existingQuotaInterval;
  }, [apiPlanUuid, payload, apiPlanDetails, enableRateLimitCheck, enableQuotaCheck]);

  const disableActionButtons = (enableRateLimitCheck && !payload?.rateLimit) ||
    (enableQuotaCheck && !payload.quota)

  return (
    <div className={classes.content}>
      <AlertDialog
        isOpen={showUnsavedDialog}
        title={intl.getI18nMessage('label.dialog.unsaved.changes.title')}
        description={intl.getI18nMessage('label.dialog.unsaved.changes.description')}
        submitText={PROCEED_TEXT}
        cancelText={intl.getI18nMessage('label.dialog.unsaved.changes.cancel')}
        onClose={onDialogClose}
        onSubmit={onDialogSubmit}
        onCancel={onDialogClose}
        dialogId={'api-plans-unsaved-changes'}
      />
      {isLoading && <Loading pageLoader />}
      {
        alertMessages?.status &&
        <AlertMessages
          id="add-api-plans-client-snackbar"
          className={classes.alertMessagesContainer}
          message={alertMessages.message}
          variant={alertMessages.variant}
          onClose={() => setAlertMessages({ ...alertMessages, status: false })}
          shouldAutoCloseSuccessMessages={alertMessages.variant === ALERT_SUCCESS}
        />
      }
      <Header
        intl={intl}
        classes={classes}
        payload={payload}
        setPayload={setPayload}
        apiPlanUuid={apiPlanUuid}
        apiPlanNameFromApi={apiPlanDetails?.name}
        isApiPlanDeleteSuccess={isApiPlanDeleteSuccess}
        setAlertMessages={setAlertMessages}
      />
      <Grid container className={classes.gridContainer}>
        <TabsContainer
          id="add-api-plans-details-tab-container"
          tabValue={currentTab}
          orientation="horizontal"
          tabItems={tabItems.filter(({ visible }) => visible)}
          handleTabChange={handleTabChange}
          tabActiveClass={classes.tabActive}
          tabIndicatorClass={classes.tabIndicator}
          useTabId
        />
      </Grid>
      <Divider className={classes.divider} />
      <Grid container className={classes.bodyContent}>
        <TabPanel visible={currentTab === 'add-api-plans-overview-tab'} id='add-api-plans-overview-tab'>
          <OverView
            intl={intl}
            classes={classes}
            enableQuotaCheck={enableQuotaCheck}
            setEnableQuotaCheck={setEnableQuotaCheck}
            setEnableRateLimitCheck={setEnableRateLimitCheck}
            enableRateLimitCheck={enableRateLimitCheck}
            payload={payload}
            setPayload={setPayload}
            apiPlanUuid={apiPlanUuid}
            apiPlanDetails={apiPlanDetails}
            currentTab={currentTab}
          />
        </TabPanel>
        <TabPanel visible={currentTab === 'add-api-plans-apis-tab'} id='add-api-plans-apis-tab'>
          <Apis
            apiPlanUuid={apiPlanUuid}
            setAlertMessages={setAlertMessages}
            setCurrentTab={setCurrentTab}
            setCurrentTabDirty={setAPIsTabDirty}
          />
        </TabPanel>
        <TabPanel visible={currentTab === 'add-api-plans-organizations-tab'} id='add-api-plans-organizations-tab'>
          <Organizations
            apiPlanUuid={apiPlanUuid}
            setAlertMessages={setAlertMessages}
            setCurrentTab={setCurrentTab}
            setCurrentTabDirty={setOrgsTabDirty}
          />
        </TabPanel>
        {
          isOverviewTabDirty &&
          <FormActionButtons
            id='add-api-plans-overview-action-buttons'
            nextText={intl.getI18nMessage('label.save.button')}
            onNextClick={handleSaveApiPlan}
            onCancelClick={handleCancel}
            showCancel
            isContainerFooter
            isDisabled={disableActionButtons}
          />
        }
      </Grid>
    </div>
  )
}

export default ApiPlanDetails;
